import { DATA_MAPPER_ROUTE } from "../shared/editorRoutes";

/**
 * The Data Mapper wizard: the order of the steps, what each one asks of the
 * person, and how far a profile has got. Pure, so the stepper, the store and
 * the tests read one definition.
 *
 * Each step keeps the route it always had (deep links, route hooks and the
 * e2e run still work). The wizard is the frame around them: one nav entry,
 * a stepper, the step's instructions, and Back / Next.
 */

/** Every route the wizard frames, by name (RouterStore.goTo targets). */
export const WIZARD_ROUTE = {
  profiles: DATA_MAPPER_ROUTE.profiles,
  source: DATA_MAPPER_ROUTE.source,
  restore: "Data Mapper Restore",
  browse: "Data Mapper Browse",
  explore: "Data Mapper Explore",
  bulkExport: "Data Mapper Bulk Export",
  schema: DATA_MAPPER_ROUTE.schema,
  library: DATA_MAPPER_ROUTE.library,
  map: DATA_MAPPER_ROUTE.map,
  transform: DATA_MAPPER_ROUTE.transform,
  lookupTables: DATA_MAPPER_ROUTE.lookupTables,
  target: DATA_MAPPER_ROUTE.target,
  import: DATA_MAPPER_ROUTE.import,
} as const;

export type WizardStepKey =
  | "profile"
  | "source"
  | "review"
  | "export"
  | "schema"
  | "map"
  | "transform"
  | "target"
  | "import";

/** One page inside a step, shown as a tab when a step has more than one. */
export interface WizardPage {
  route: string;
  label: string;
  /** Shown only to administrators (the API refuses everyone else). */
  adminOnly?: boolean;
}

/** A short warning about client data, shown on the steps where it applies. */
export interface WizardDataNotice {
  title: string;
  text: string;
}

export interface WizardStepDef {
  key: WizardStepKey;
  /** The stepper's label: one or two words. */
  label: string;
  /** The heading above the step's instructions. */
  title: string;
  /** One sentence: what this step is for. */
  summary: string;
  /** What to do, in order. */
  instructions: string[];
  /** The step's pages; the first is where the stepper takes you. */
  pages: WizardPage[];
  /** A step that can be skipped. Titan profiles also mark the hand-mapping steps optional. */
  optional?: boolean;
  /** Present on steps where client data leaves the database or reaches an AI provider. */
  dataNotice?: WizardDataNotice;
}

const AI_NOTICE_TEXT =
  "The AI buttons on this step send column names, types and statistics to the AI provider that HQ routes the task to. " +
  "If the table holds employee, benefits or health data, do not use them. Follow the HIPAA and AI guide.";

export const WIZARD_STEPS: Record<WizardStepKey, WizardStepDef> = {
  profile: {
    key: "profile",
    label: "Profile",
    title: "Open or create a profile",
    summary: "A profile holds one data migration's work for one client company: its source, schema, mappings and progress.",
    instructions: [
      "Open a profile from the list. Search by profile or client, or pick a client in the header to see only its profiles (with its child companies').",
      "Or select New Profile and choose the client company it is for. Each client company has its own profiles; plants share one only when their Titan setup is the same.",
      "If the source is a Titan database, turn on \"The source is a Titan backup\" in the profile form. That adds the Bulk Export step.",
    ],
    pages: [{ route: WIZARD_ROUTE.profiles, label: "Profiles" }],
  },
  source: {
    key: "source",
    label: "Source",
    title: "Connect the source data",
    summary: "Connect this browser tab to the database or file you are reading from.",
    instructions: [
      "Choose the source type: SQL Server, a CSV, Excel or JSON file, or a REST API.",
      "For SQL Server, enter the server and database. Use localhost for a database restored on your own machine (your Data Mapper connector must be running).",
      "Type the password, select Test, then Connect. Passwords are never saved; you type them each time.",
      "Select Save connection to keep the server and database (without the password) for next time.",
      "Administrators can restore a client's .BAK backup on the Restore backup tab first.",
    ],
    pages: [
      { route: WIZARD_ROUTE.source, label: "Connect" },
      { route: WIZARD_ROUTE.restore, label: "Restore backup", adminOnly: true },
    ],
  },
  review: {
    key: "review",
    label: "Review",
    title: "Review the source data",
    summary: "Look at the tables and their data quality before you map anything.",
    instructions: [
      "Browse: open a table to page through its rows, and filter them.",
      "Explore: profile a table to see each column's empty values, distinct values and quality flags.",
      "Note what needs cleaning: bad dates, text in number columns, codes that need translating.",
      "Only open tables you need. Do not profile or save reports for employee, benefits or health tables.",
    ],
    pages: [
      { route: WIZARD_ROUTE.browse, label: "Browse rows" },
      { route: WIZARD_ROUTE.explore, label: "Explore and profile" },
    ],
    optional: true,
    dataNotice: {
      title: "Client data on screen",
      text:
        "This step shows real client rows. Saved explore reports keep column statistics, including the most common values, in HQ. " +
        AI_NOTICE_TEXT,
    },
  },
  export: {
    key: "export",
    label: "Bulk Export",
    title: "Export the Titan tables",
    summary: "Turn every mapped Titan table into IntraSync's JSON format, in one zip.",
    instructions: [
      "Check the source summary: it should find most of the Titan tables. If it does not, you are connected to the wrong database.",
      "Select Map all to generate the automatic mappings for every table.",
      "Select Dry run all. It reads and checks every row and writes nothing.",
      "Fix what the dry run reports, then select Export all and download the zip.",
      "Read EXPORT-REPORT.md in the zip. Store the zip only in the client's approved location, and delete local copies when you are done.",
    ],
    pages: [{ route: WIZARD_ROUTE.bulkExport, label: "Bulk Export" }],
    dataNotice: {
      title: "The zip is client data",
      text:
        "The export holds the client's rows. HQ deletes its copy after you download it. Payroll tables are not exported. " +
        "Never email the zip, upload it to an AI tool, or keep it on a personal device.",
    },
  },
  schema: {
    key: "schema",
    label: "Schema",
    title: "Choose the target schema",
    summary: "The schema lists the fields the data must end up in.",
    instructions: [
      "Pick a saved schema, or select New schema and paste the TypeBox schema.",
      "Save it, then select Map this schema.",
      "The Library tab lists every schema and lookup table shared with this profile.",
    ],
    pages: [
      { route: WIZARD_ROUTE.schema, label: "Target schema" },
      { route: WIZARD_ROUTE.library, label: "Library" },
    ],
  },
  map: {
    key: "map",
    label: "Map",
    title: "Map source columns to target fields",
    summary: "Say which source column fills each target field.",
    instructions: [
      "Choose or create a mapping configuration at the top.",
      "Set the source table, then pick a source column for each target field.",
      "Every required field needs a column. Leave out fields that nothing fits.",
      "Select Save. Import runs the saved configuration.",
    ],
    pages: [{ route: WIZARD_ROUTE.map, label: "Map fields" }],
    dataNotice: { title: "AI suggestions", text: AI_NOTICE_TEXT },
  },
  transform: {
    key: "transform",
    label: "Transform",
    title: "Clean and convert values",
    summary: "Add steps that fix values on the way: trim, change case, convert types, look up codes.",
    instructions: [
      "Open a mapped field to see its transform chain.",
      "Add steps in order. The preview shows what a sample value becomes.",
      "To translate codes (B to beam), create a table on the Lookup tables tab, then add a Value Lookup step.",
      "Select Save when the chain is right.",
    ],
    pages: [
      { route: WIZARD_ROUTE.transform, label: "Transforms" },
      { route: WIZARD_ROUTE.lookupTables, label: "Lookup tables" },
    ],
    optional: true,
    dataNotice: {
      title: "Examples go to the AI provider",
      text:
        "The regex helper sends the example values you type to the AI provider. Type made-up examples in the same format. Never paste a real name, SSN, birth date or health detail.",
    },
  },
  target: {
    key: "target",
    label: "Target",
    title: "Connect the target database",
    summary: "Connect this tab to the database the import writes into.",
    instructions: [
      "Enter the target server and database. Use a test or staging database first.",
      "Type the password, select Test, then Connect.",
      "Check the table list: the target tables must already exist.",
    ],
    pages: [{ route: WIZARD_ROUTE.target, label: "Target database" }],
  },
  import: {
    key: "import",
    label: "Import",
    title: "Dry run, then write",
    summary: "Run the saved mapping from the source into the target.",
    instructions: [
      "Choose the mapping configuration and check each source table's target table.",
      "Select Dry run. It reads, transforms and checks every row and writes nothing.",
      "Fix the invalid rows it reports, at the source or with a transform, and dry-run again.",
      "When the dry run is clean, choose the write mode and select Write. The whole write is one transaction.",
    ],
    pages: [{ route: WIZARD_ROUTE.import, label: "Import" }],
  },
};

/** The steps for a profile, in order. Titan profiles get Bulk Export, and the hand-mapping steps become optional. */
export function wizardStepsFor(isTitan: boolean): WizardStepDef[] {
  const handMapping: WizardStepKey[] = ["schema", "map", "transform", "target", "import"];
  const keys: WizardStepKey[] = isTitan
    ? ["profile", "source", "review", "export", ...handMapping]
    : ["profile", "source", "review", ...handMapping];
  return keys.map((key) => {
    const def = WIZARD_STEPS[key];
    return isTitan && handMapping.includes(key) ? { ...def, optional: true } : def;
  });
}

/** The step a route belongs to, or null for a route outside the wizard. */
export function stepKeyForRoute(routeName: string | null | undefined): WizardStepKey | null {
  if (!routeName) return null;
  for (const def of Object.values(WIZARD_STEPS)) {
    if (def.pages.some((p) => p.route === routeName)) return def.key;
  }
  return null;
}

/** Where a profile stands, as the wizard reads it. */
export interface WizardProgress {
  hasProfile: boolean;
  sourceConnected: boolean;
  targetConnected: boolean;
  hasSchema: boolean;
  hasMappingConfig: boolean;
}

export type WizardStepState = "done" | "current" | "todo" | "optional" | "locked";

/** A step's state for the stepper: done, the one open, still to do, optional, or locked (no profile yet). */
export function stepState(key: WizardStepKey, current: WizardStepKey | null, progress: WizardProgress, optional = false): WizardStepState {
  if (key === current) return "current";
  if (key !== "profile" && !progress.hasProfile) return "locked";
  if (isStepDone(key, progress)) return "done";
  return optional ? "optional" : "todo";
}

/** Whether a step's own work is finished. Steps with no fixed end (Review, Transform, Import, Bulk Export) never are. */
export function isStepDone(key: WizardStepKey, progress: WizardProgress): boolean {
  switch (key) {
    case "profile":
      return progress.hasProfile;
    case "source":
      return progress.sourceConnected;
    case "schema":
      return progress.hasSchema;
    case "map":
      return progress.hasMappingConfig;
    case "target":
      return progress.targetConnected;
    default:
      return false;
  }
}

/** Something a step needs from an earlier one, with the step that provides it. */
export interface WizardBlocker {
  text: string;
  step: WizardStepKey;
}

/** What is missing before this step can do its work. Empty when nothing is. */
export function blockersFor(key: WizardStepKey, progress: WizardProgress): WizardBlocker[] {
  if (key === "profile") return [];
  if (!progress.hasProfile) return [{ text: "Open a profile first.", step: "profile" }];
  const out: WizardBlocker[] = [];
  const needSource = () => {
    if (!progress.sourceConnected) out.push({ text: "This tab has no source connected.", step: "source" });
  };
  // Review and Bulk Export are not listed: their own pages say there is no source, with a link.
  switch (key) {
    case "map":
      if (!progress.hasSchema) out.push({ text: "Choose a target schema first.", step: "schema" });
      break;
    case "transform":
      if (!progress.hasMappingConfig) out.push({ text: "Save a mapping configuration first.", step: "map" });
      break;
    case "import":
      needSource();
      if (!progress.hasMappingConfig) out.push({ text: "Save a mapping configuration first.", step: "map" });
      if (!progress.targetConnected) out.push({ text: "This tab has no target connected.", step: "target" });
      break;
    default:
      break;
  }
  return out;
}
